/**
 * @fileoverview Evidence Packet Dialog - Step 1 to Step 3 Gate with Transition Video
 * @module EvidencePacketDialog
 * 
 * Business Context:
 * - Gates progression from Step 1 (Document) to Step 3 (Find Flaws)
 * - Starts with a transition video explaining the waiting period
 * - Asks if user has received their evidence packet from the county
 * - Captures informal meeting date if provided
 * - Allows skipping to continue later
 * 
 * Technical Context:
 * - Modal dialog with multi-step flow
 * - Saves to AppealOutcome model
 * - Integrates with apiClient.appealOutcome.save()
 */

import React, { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { apiClient } from '../../api/client';
import VideoPlayer from '../course/VideoPlayer';
import {
  X,
  Package,
  Calendar,
  AlertCircle,
  ChevronRight,
  Check,
  Clock,
  HelpCircle,
  Play,
  SkipForward
} from 'lucide-react';

interface EvidencePacketDialogProps {
  isOpen: boolean;
  propertyId: string;
  transitionVideoUrl?: string | undefined;
  onClose: () => void;
  onContinue: () => void;
  onSkip: () => void;
}

type DialogStep = 'video' | 'received' | 'hearing-date' | 'not-received';

export const EvidencePacketDialog: React.FC<EvidencePacketDialogProps> = ({
  isOpen,
  propertyId,
  transitionVideoUrl,
  onClose,
  onContinue,
  onSkip
}) => {
  // Start at 'video' step if we have a transition video URL, otherwise 'received'
  const initialStep: DialogStep = transitionVideoUrl ? 'video' : 'received';
  
  const [step, setStep] = useState<DialogStep>(initialStep);
  const [hasReceivedPacket, setHasReceivedPacket] = useState<boolean | null>(null);
  const [hasHearingDate, setHasHearingDate] = useState<boolean | null>(null);
  const [hearingDate, setHearingDate] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [videoWatched, setVideoWatched] = useState(false);

  if (!isOpen) return null;

  const handleVideoComplete = () => {
    setVideoWatched(true);
  };

  const handleVideoSkip = () => {
    setStep('received');
  };

  const handleVideoContinue = () => {
    setStep('received');
  };

  const handleReceivedResponse = (received: boolean) => {
    setHasReceivedPacket(received);
    if (received) {
      setStep('hearing-date');
    } else {
      setStep('not-received');
    }
  };

  const handleHearingDateResponse = (hasDate: boolean) => {
    setHasHearingDate(hasDate);
  };

  const handleSaveAndContinue = async () => {
    setSaving(true);
    setError(null);

    try {
      const data: any = {
        propertyId,
        appealYear: new Date().getFullYear(),
        evidencePacketReceived: hasReceivedPacket === true
      };

      // Add hearing date if provided
      if (hearingDate) {
        data.hearingDate = new Date(hearingDate).toISOString();
      }

      await apiClient.appealOutcome.save(data);
      onContinue();
    } catch (err) {
      console.error('Error saving evidence packet info:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    setSaving(true);
    setError(null);

    try {
      await apiClient.appealOutcome.save({
        propertyId,
        appealYear: new Date().getFullYear(),
        evidencePacketSkipped: true
      });
      onSkip();
    } catch (err) {
      console.error('Error saving skip status:', err);
      // Still allow continuing even if save fails
      onSkip();
    } finally {
      setSaving(false);
    }
  };

  const resetDialog = () => {
    setStep(transitionVideoUrl ? 'video' : 'received');
    setHasReceivedPacket(null);
    setHasHearingDate(null);
    setHearingDate('');
    setError(null);
    setVideoWatched(false);
  };

  const handleClose = () => {
    resetDialog();
    onClose();
  };

  // Get minimum date (today) for date picker
  const getMinDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Get max date (1 year from now)
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 1);
    return maxDate.toISOString().split('T')[0];
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div 
        className={`bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] overflow-y-auto ${
          step === 'video' ? 'max-w-2xl' : 'max-w-lg'
        }`}
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: 'DM Sans, system-ui, sans-serif' }}
      >
        {/* Header - Different for video step */}
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                step === 'video' ? 'bg-emerald-100' : 'bg-teal-100'
              }`}>
                {step === 'video' ? (
                  <Play className="w-5 h-5 text-emerald-600" />
                ) : (
                  <Package className="w-5 h-5 text-teal-600" />
                )}
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">
                  {step === 'video' ? "What's Next" : 'Before You Continue'}
                </h2>
                <p className="text-sm text-slate-500">
                  {step === 'video' 
                    ? 'Watch this quick video about next steps' 
                    : 'Quick check about your evidence packet'
                  }
                </p>
              </div>
            </div>
            <button 
              onClick={handleClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Video Step - NEW */}
          {step === 'video' && transitionVideoUrl && (
            <div className="space-y-4">
              {/* Video Player */}
              <div className="rounded-xl overflow-hidden bg-slate-900">
                <VideoPlayer
                  videoUrl={transitionVideoUrl}
                  currentProgress={0}
                  onProgress={() => {}}
                  onVideoComplete={handleVideoComplete}
                />
              </div>

              {/* Video Info */}
              <div className="text-center">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  What's Next: Preparing for Step 3
                </h3>
                <p className="text-slate-600 text-sm max-w-md mx-auto">
                  Great job completing your documentation! This video explains what happens next 
                  in the appeal process and what to do while waiting for the assessor's evidence.
                </p>
              </div>

              {/* Continue Button */}
              <div className="pt-2 space-y-3">
                <button
                  onClick={handleVideoContinue}
                  className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold transition-colors ${
                    videoWatched
                      ? 'bg-teal-600 hover:bg-teal-700 text-white'
                      : 'bg-slate-900 hover:bg-slate-800 text-white'
                  }`}
                >
                  {videoWatched ? (
                    <>
                      Continue
                      <ChevronRight className="w-5 h-5" />
                    </>
                  ) : (
                    <>
                      Continue
                      <ChevronRight className="w-5 h-5" />
                    </>
                  )}
                </button>

                <button
                  onClick={handleVideoSkip}
                  className="w-full flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors py-2"
                >
                  <SkipForward className="w-4 h-4" />
                  Skip video
                </button>
              </div>
            </div>
          )}

          {/* Step 1: Have you received evidence packet? */}
          {step === 'received' && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Package className="w-8 h-8 text-slate-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  Have you received your evidence packet?
                </h3>
                <p className="text-slate-600 text-sm max-w-sm mx-auto">
                  After filing your protest, the county sends an evidence packet with their valuation data. 
                  This usually arrives 2-4 weeks before your informal meeting.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleReceivedResponse(true)}
                  className="p-4 rounded-xl border-2 border-slate-200 hover:border-teal-500 hover:bg-teal-50 transition-all text-left group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                      <Check className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Yes, I have it</p>
                      <p className="text-xs text-slate-500">Let's continue</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleReceivedResponse(false)}
                  className="p-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-left group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Not yet</p>
                      <p className="text-xs text-slate-500">Still waiting</p>
                    </div>
                  </div>
                </button>
              </div>

              <div className="mt-6 pt-4 border-t border-slate-100">
                <button
                  onClick={handleSkip}
                  disabled={saving}
                  className="w-full text-center text-sm text-slate-500 hover:text-slate-700 transition-colors"
                >
                  Skip for now — I'll enter this later
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Hearing date question */}
          {step === 'hearing-date' && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  Does your packet include a meeting date?
                </h3>
                <p className="text-slate-600 text-sm max-w-sm mx-auto">
                  Your evidence packet should include your informal meeting date — this is when you'll 
                  meet with an appraiser about your property value.
                </p>
              </div>

              {hasHearingDate === null ? (
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleHearingDateResponse(true)}
                    className="p-4 rounded-xl border-2 border-slate-200 hover:border-teal-500 hover:bg-teal-50 transition-all text-left group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                        <Check className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Yes</p>
                        <p className="text-xs text-slate-500">I have a date</p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handleHearingDateResponse(false)}
                    className="p-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-left group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                        <HelpCircle className="w-5 h-5 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">No / Not sure</p>
                        <p className="text-xs text-slate-500">Continue anyway</p>
                      </div>
                    </div>
                  </button>
                </div>
              ) : hasHearingDate ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Your Informal Meeting Date
                    </label>
                    <input
                      type="date"
                      value={hearingDate}
                      onChange={(e) => setHearingDate(e.target.value)}
                      min={getMinDate()}
                      max={getMaxDate()}
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-lg"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      We'll help you prepare before this date
                    </p>
                  </div>

                  <button
                    onClick={handleSaveAndContinue}
                    disabled={saving || !hearingDate}
                    className="w-full flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 py-4 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Saving...' : 'Continue to Step 3'}
                    <ChevronRight className="w-5 h-5" />
                  </button>

                  <button
                    onClick={() => setHasHearingDate(null)}
                    className="w-full text-center text-sm text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    Go back
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <p className="text-sm text-slate-600">
                      No problem! You can add your meeting date later from the dashboard. 
                      The county should send you a notice with the scheduled date.
                    </p>
                  </div>

                  <button
                    onClick={handleSaveAndContinue}
                    disabled={saving}
                    className="w-full flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 py-4 rounded-xl transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Continue to Step 3'}
                    <ChevronRight className="w-5 h-5" />
                  </button>

                  <button
                    onClick={() => setHasHearingDate(null)}
                    className="w-full text-center text-sm text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    Go back
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Not received state */}
          {step === 'not-received' && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  That's okay!
                </h3>
                <p className="text-slate-600 text-sm max-w-sm mx-auto">
                  Evidence packets typically arrive 2-4 weeks before your meeting. 
                  You can still continue to Step 3, but the county's evidence will help you identify flaws.
                </p>
              </div>

              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900 mb-1">
                      Where to check for your packet
                    </p>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li>• Check your physical mailbox</li>
                      <li>• Log into your county's online portal</li>
                      <li>• Contact your County Appraisal District</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <button
                  onClick={() => setStep('received')}
                  className="px-4 py-3 border border-slate-200 rounded-lg text-slate-600 font-medium hover:bg-slate-50 transition-colors"
                >
                  Go Back
                </button>
                <button
                  onClick={handleSaveAndContinue}
                  disabled={saving}
                  className="flex items-center justify-center gap-2 bg-slate-900 text-white px-4 py-3 rounded-lg font-medium hover:bg-slate-800 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Continue Anyway'}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EvidencePacketDialog;
